import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Communication Provenance Timeline — lean, READ-ONLY audit surface.
// Reuses immutable provenance already produced by governed senders. No editing / resend / regenerate.
// Two independent axes: LIFECYCLE (active/shadow/retired) vs GOVERNANCE (canonical/legacy/excluded).

const GOV_BADGE = {
  canonical: { label: "Governed", dot: "bg-emerald-500", text: "text-emerald-700", ring: "bg-emerald-50 border-emerald-200" },
  legacy: { label: "Legacy", dot: "bg-slate-400", text: "text-slate-600", ring: "bg-slate-50 border-slate-200" },
  excluded: { label: "Excluded", dot: "bg-slate-800", text: "text-slate-800", ring: "bg-slate-100 border-slate-300" },
};

function GovBadge({ status }) {
  const b = GOV_BADGE[status] || GOV_BADGE.legacy;
  return (
    <span data-testid={`prov-badge-${status}`}
      className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border", b.ring, b.text)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", b.dot)} />
      {b.label}
    </span>
  );
}

function Fp({ label, value }) {
  if (!value) return null;
  const short = value.length > 24 ? `${value.slice(0, 20)}…` : value;
  const copy = () => { navigator.clipboard?.writeText(value); toast.success("Copied"); };
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
      <button type="button" onClick={copy} data-testid="prov-copy-fp"
        className="font-mono text-[11px] text-slate-600 hover:text-brand-gold inline-flex items-center gap-1">
        {short}<Icons.Copy className="h-3 w-3" />
      </button>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
      <span className="text-xs text-slate-700 text-right">{value}</span>
    </div>
  );
}

function ProvenanceDrawer({ communicationId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/communications/provenance/${encodeURIComponent(communicationId)}`)
      .then((r) => { if (alive) setDetail(r.data); })
      .catch(() => { if (alive) setDetail(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [communicationId]);

  if (loading) return <div className="px-4 py-3 text-xs text-slate-400" data-testid="prov-drawer-loading">Loading provenance…</div>;
  if (!detail) return <div className="px-4 py-3 text-xs text-rose-500">Provenance unavailable.</div>;

  const p = detail.provenance || {};
  const f = detail.fingerprints || {};
  const protectedFps = Object.entries(f.protected_component_fingerprints || {});
  const sectionFps = Object.entries(f.section_fingerprints || {});
  return (
    <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 grid gap-4 md:grid-cols-3" data-testid="prov-drawer">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Template</div>
        <DetailRow label="Template ID" value={detail.template?.template_id} />
        <DetailRow label="Version" value={detail.template?.template_version} />
        <DetailRow label="Legal Significance" value={detail.template?.legal_significance} />
        <DetailRow label="Lifecycle" value={detail.lifecycle_status} />
        <DetailRow label="Governance" value={detail.governance_status} />
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Provenance</div>
        <DetailRow label="Generated At" value={p.generated_at ? new Date(p.generated_at).toLocaleString() : null} />
        <DetailRow label="Generated By" value={p.generated_by} />
        <DetailRow label="Renderer" value={p.renderer_version} />
        <DetailRow label="Content Source" value={p.content_source} />
        <DetailRow label="Certification" value={p.certification_version} />
        <DetailRow label="Adapter" value={p.adapter_id} />
        <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Routing</div>
        <DetailRow label="Classification" value={detail.routing?.classification} />
        <div className="text-[11px] text-slate-500 mt-0.5">{detail.routing?.path}</div>
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Fingerprints</div>
        <Fp label="Content" value={f.content_fingerprint} />
        <Fp label="Document" value={f.document_content_fingerprint} />
        {protectedFps.map(([k, v]) => <Fp key={k} label="Protected §" value={v} />)}
        {sectionFps.slice(0, 4).map(([k, v]) => <Fp key={k} label={k} value={v} />)}
        <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Delivery</div>
        <div className="flex flex-wrap gap-1">
          {(detail.delivery?.channels || []).map((c) => (
            <span key={c} className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[11px] text-slate-600">{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function TimelineRow({ entry }) {
  const [open, setOpen] = useState(false);
  const Icon = Icons[entry.icon] || Icons.Mail;
  const when = entry.occurred_at ? new Date(entry.occurred_at) : null;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="prov-timeline-row">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="prov-row-toggle"
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
        <span className="h-8 w-8 shrink-0 rounded-md bg-slate-100 flex items-center justify-center text-slate-500">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-800 truncate">{entry.comm_kind}</span>
            <GovBadge status={entry.governance_status} />
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            {when && <span>{when.toLocaleDateString()} · {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
            {entry.template_id && <span className="font-mono text-[11px] text-slate-400">{entry.template_id}{entry.template_version ? ` v${entry.template_version}` : ""}</span>}
            {(entry.delivery || []).length > 0 && <span className="text-slate-400">· {entry.delivery.join(" + ")}</span>}
          </div>
        </div>
        <Icons.ChevronDown className={cn("h-4 w-4 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>
      {open && <ProvenanceDrawer communicationId={entry.communication_id} />}
    </div>
  );
}

export default function CommProvenanceTimeline({ clientId, engagementId = null, title = "Communication Provenance", scopeLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comm_type, setCommType] = useState("");
  const [governance, setGovernance] = useState("");

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    const params = {};
    if (engagementId) params.engagement_id = engagementId;
    if (comm_type) params.comm_type = comm_type;
    if (governance) params.governance = governance;
    api.get(`/communications/provenance-timeline/${clientId}`, { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], summary: { total: 0, governed: 0, legacy: 0, excluded: 0 } }))
      .finally(() => setLoading(false));
  }, [clientId, engagementId, comm_type, governance]);

  useEffect(() => { load(); }, [load]);

  const entries = data?.entries || [];
  const s = data?.summary || { total: 0, governed: 0, legacy: 0, excluded: 0 };

  return (
    <div className="space-y-4" data-testid="comm-provenance-timeline">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Icons.ShieldCheck className="h-5 w-5 text-brand-gold" />{title}
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Read-only audit of governed communications{scopeLabel ? ` · ${scopeLabel}` : ""}. Which template produced each, and under which governed authority.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500" data-testid="prov-summary">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />{s.governed} Governed</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-400" />{s.legacy} Legacy</span>
          {s.excluded > 0 && <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-800" />{s.excluded} Excluded</span>}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="prov-filter-type" value={comm_type} onChange={(e) => setCommType(e.target.value)}
          className="bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
          <option value="">All types</option>
          <option value="engagement_letter">Engagement Letters</option>
          <option value="email">Emails</option>
        </select>
        <select data-testid="prov-filter-governance" value={governance} onChange={(e) => setGovernance(e.target.value)}
          className="bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
          <option value="">All governance</option>
          <option value="canonical">Governed</option>
          <option value="legacy">Legacy</option>
          <option value="excluded">Excluded</option>
        </select>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="prov-loading">Loading timeline…</div>
      ) : entries.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg" data-testid="prov-empty">
          No governed communications recorded yet for this {engagementId ? "engagement" : "client"}.
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => <TimelineRow key={e.communication_id} entry={e} />)}
        </div>
      )}
    </div>
  );
}
